import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DraggableTaskCard } from './DraggableTaskCard';
import { TaskItem } from '../../../../types/topic';
import { cn } from '../../../../lib/utils';
import { TaskForm } from '../TaskForm';
import { Mic, Plus } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Types } from './taskTypes';
import { ColumnMenu } from '../ColumnMenu';
import { extractBaseColor, getStatusColor } from '../../../../utils/colorUtils';
import { EmptyTaskState } from '../EmptyTaskState';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { FormattedTaskColumn } from '../../../../hooks/queries/taskColumnTypes';
import { COLUMN_TO_BORDER_COLOR_MAP } from '../../../../constants/colorConstants';
import { getColumnTextColorClass } from '../utils/columnUtils';

// Helper to get text color from column color moved to ../utils/columnUtils.ts

interface DroppableTaskColumnProps {
  column: FormattedTaskColumn;
  items: TaskItem[];
  projectId: string; // Added project ID
  topicId: string; // Topic ID for fetching columns
  isRecording: boolean;
  isCreating: boolean;
  activeColumn: TaskItem['status'];
  title: string;
  description: string;
  tags: string[];
  deadline: Date | null;
  showDescription: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  itemToDelete: string | null;
  totalTaskCount: number;
  isEditingColumnName: string | null;
  isEditingColumnColor: string | null;
  className?: string;
  activeTask?: string | null; // ID of task being dragged
  hasCollaborators?: boolean; // Whether the project has collaborators
  // Filter props
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
  onVoiceClick: () => void;
  onAddClick: () => void;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTagsChange: (value: string[]) => void;
  onDeadlineChange: (value: Date | null) => void;
  onAssigneeChange?: (value: string | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
  moveTask: (id: string, toIndex: number) => void;
  moveTaskBetweenColumns: (id: string, newStatus: TaskItem['status'], newIndex: number) => void;
  onEditColumnNameClick: (columnId: string) => void;
  onEditColumnName: (columnId: string, name: string) => void;
  onDeleteColumnClick: (columnId: string) => void;
  onColumnColorSelect: (columnId: string, color: string) => void;
  onAddColumnAfter: (columnId: string) => void;
  onMoveColumnLeft: (columnId: string) => void;
  onMoveColumnRight: (columnId: string) => void;
  forceTaskMove?: (taskId: string, targetColumnStatus: string) => void;
  // Processed data from container (eliminates business logic)
  availableTags?: string[];
  columns?: FormattedTaskColumn[];
  taskIds?: string[];
}

export const DroppableTaskColumn: React.FC<DroppableTaskColumnProps> = ({
  column,
  items,
  projectId,
  isRecording,
  isCreating,
  activeColumn,
  title,
  description,
  tags,
  deadline,
  showDescription,
  inputRef,
  itemToDelete,
  totalTaskCount,
  isEditingColumnName,
  hasCollaborators = false,
  className,
  activeTask,
  // Filter props
  showUnscheduledOnly,
  showUnassignedOnly,
  userFilters,
  onVoiceClick,
  onAddClick,
  onDelete,
  onDeleteClick,
  onCancelDelete,
  onUpdateTask,
  onTitleChange,
  onDescriptionChange,
  onTagsChange,
  onDeadlineChange,
  onAssigneeChange,
  onSubmit,
  onCancel,
  moveTask,
  onEditColumnNameClick,
  onEditColumnName,
  onDeleteColumnClick,
  onColumnColorSelect,
  onAddColumnAfter,
  onMoveColumnLeft,
  onMoveColumnRight,
  // Processed data from container
  availableTags = [],
  columns = [],
  taskIds = [],
}) => {
  // Calculate if this column is first or last for move buttons
  const columnIndex = columns.findIndex(c => c.id === column.id);
  const isFirstColumn = columnIndex === 0;
  const isLastColumn = columnIndex === columns.length - 1;
  // State for column name editing
  const [editingName, setEditingName] = useState(column.name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing column name
  useEffect(() => {
    if (isEditingColumnName === column.id && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingColumnName, column.id]);

  // Use column.status as the droppableId - this is what tasks.status refers to
  const droppableId = column.status;

  const { setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: Types.COLUMN,
      status: column.status,
      id: column.id,
      name: column.name,
    },
  });

  // When a task is moved within the same column
  const handleMoveTask = useCallback(
    (taskId: string, toIndex: number) => {
      moveTask(taskId, toIndex);
    },
    [moveTask]
  );

  // Handle column name edit with better optimistic UI
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only update if the name actually changed and isn't empty
    const newName = editingName.trim();
    if (newName !== column.name && newName !== '') {
      // Send the update to the database
      onEditColumnName(column.id, newName);
    } else if (newName === '') {
      // Reset to original name if empty
      setEditingName(column.name);
      onEditColumnName(column.id, column.name);
    } else {
      // No change, just exit edit mode
      onEditColumnName(column.id, column.name);
    }
  };

  // Reset editing name when column changes
  useEffect(() => {
    setEditingName(column.name);
  }, [column.name]);

  // Data is now processed by container component - this component is purely presentational

  return (
    <div
      className={cn(
        'flex flex-col h-full relative z-10',
        className,
        activeTask && 'z-30' // Increase z-index during drag
      )}
      style={{
        contain: 'layout', // Isolate layout calculations
        willChange: 'transform', // Hint for browser optimization
      }}
    >
      {/* Column header */}
      <div className="mb-3 p-2 flex items-center justify-between flex-shrink-0 rounded-md relative z-[100]">
        <div className="flex items-center gap-2 max-w-[75%]">
          {isEditingColumnName === column.id ? (
            <form onSubmit={handleNameSubmit} className="flex-1 flex items-center">
              <input
                ref={nameInputRef}
                type="text"
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={handleNameSubmit}
                className="w-full bg-transparent font-bold font-sora focus:outline-none"
                style={{
                  // Match input color to the column color
                  color: (() => {
                    if (!column.color) return 'text-gray-900';

                    const baseColor = extractBaseColor(column.color);
                    if (baseColor) {
                      const borderColor = COLUMN_TO_BORDER_COLOR_MAP[baseColor];
                      if (borderColor) {
                        return borderColor.replace('border-', 'text-');
                      }
                    }

                    // Try direct mapping
                    const directColor = COLUMN_TO_BORDER_COLOR_MAP[column.color];
                    if (directColor) {
                      return directColor.replace('border-', 'text-');
                    }

                    return 'text-gray-900';
                  })(),
                }}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setEditingName(column.name);
                    onEditColumnName(column.id, column.name);
                  }
                }}
              />
              <span className="inline-flex items-center justify-center h-6 min-w-[24px] rounded-full bg-white text-gray-600 text-xs font-medium px-2 flex-shrink-0 ml-2">
                {items.length}
              </span>
            </form>
          ) : (
            <h3
              className={`font-bold font-sora flex items-center gap-2 cursor-pointer truncate ${getColumnTextColorClass(column.color) || 'text-gray-900'}`}
              onClick={() => onEditColumnNameClick(column.id)}
              title="Click to edit column name"
            >
              {column.name}
              <span className="inline-flex items-center justify-center h-6 min-w-[24px] rounded-full bg-white text-gray-600 text-xs font-medium px-2 flex-shrink-0">
                {items.length}
              </span>
            </h3>
          )}
        </div>
        <div className="flex items-center gap-1 relative z-[101]">
          <ColumnMenu
            column={column}
            isFirst={isFirstColumn}
            isLast={isLastColumn}
            onDeleteClick={onDeleteColumnClick}
            onEditNameClick={onEditColumnNameClick}
            onColorSelect={onColumnColorSelect}
            onAddColumn={onAddColumnAfter}
            onMoveLeft={onMoveColumnLeft}
            onMoveRight={onMoveColumnRight}
          />
          <button
            onClick={onVoiceClick}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              isRecording
                ? 'bg-red-500 text-white hover:bg-red-600'
                : `bg-gray-100 ${getColumnTextColorClass(column.color)} hover:bg-gray-200`
            )}
            title={isRecording ? 'Stop recording' : 'Start voice recording'}
          >
            <Mic size={16} className={cn(isRecording && 'animate-pulse')} />
          </button>
          <button
            onClick={onAddClick}
            className={`p-1.5 rounded-lg bg-gray-100 ${getColumnTextColorClass(column.color)} hover:bg-gray-200 transition-colors`}
            title="Add task"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Column content - this entire div is the drop target */}
      <div
        ref={setNodeRef}
        data-column-status={column.status}
        data-column-id={column.id}
        className={cn(
          'flex-1 rounded-lg flex flex-col overflow-hidden',
          // No gradient or color for the column background
          // No backdrop-blur-xl
          // No special styles when hovering, just increase z-index during drag
          activeTask && 'z-30'
        )}
      >
        <div className="relative overflow-visible h-full flex-1">
          {/* Loading and error states are now handled by the container component */}
          {/* Tasks list with vertical scrolling only */}
          <div
            className="space-y-3 overflow-y-auto pb-0 pt-0 h-full relative"
            style={{ isolation: 'isolate', overflowX: 'visible' }}
          >
            {/* Always show form at the top when active */}
            {isCreating && activeColumn === column.status && (
              <div
                className={cn(
                  'bg-white shadow w-full px-4 pt-3 pb-2 rounded-[5px] border-l-[5px]',
                  column.color
                    ? (() => {
                        // Format current columns for consistent calculations - using already fetched columns
                        // Use a defensive approach in case columns data is still loading or empty
                        const formattedStatusColors =
                          columns.length > 0
                            ? columns.map(col => ({
                                status: col.status,
                                color: extractBaseColor(col.color) || 'gray',
                              }))
                            : [
                                { status: 'todo', color: 'blue' },
                                { status: 'doing', color: 'yellow' },
                                { status: 'done', color: 'green' },
                              ];

                        // Get border color directly using the getStatusColor utility
                        return getStatusColor(activeColumn, formattedStatusColors, 'border', false);
                      })()
                    : activeColumn === 'todo'
                      ? 'border-blue-500'
                      : activeColumn === 'doing'
                        ? 'border-yellow-500'
                        : activeColumn === 'done'
                          ? 'border-green-500'
                          : 'border-gray-500'
                )}
              >
                <TaskForm
                  isEditMode={false}
                  projectId={projectId}
                  title={title}
                  description={description}
                  tags={tags}
                  deadline={deadline}
                  status={column.status}
                  showDescription={showDescription}
                  hasCollaborators={hasCollaborators}
                  onTitleChange={onTitleChange}
                  onDescriptionChange={onDescriptionChange}
                  onTagsChange={onTagsChange}
                  onDeadlineChange={onDeadlineChange}
                  onAssigneeChange={onAssigneeChange}
                  onSubmitNewTask={onSubmit}
                  onCancel={onCancel}
                  inputRef={inputRef}
                  availableTags={availableTags}
                />
              </div>
            )}

            {/* Wrap the sortable tasks in SortableContext */}
            <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
              {items.map((item, index) => (
                <DraggableTaskCard
                  key={`${item.id}-${item.position || 0}`}
                  task={item}
                  projectId={projectId}
                  index={index}
                  isFirst={index === 0}
                  isLast={index === items.length - 1}
                  itemToDelete={itemToDelete}
                  onDelete={onDelete}
                  onDeleteClick={onDeleteClick}
                  onCancelDelete={onCancelDelete}
                  onUpdateTask={onUpdateTask}
                  moveTask={handleMoveTask}
                  hasCollaborators={hasCollaborators}
                />
              ))}
            </SortableContext>

            {/* Empty state for different filter conditions */}
            {items.length === 0 &&
              (totalTaskCount === 0 && !activeTask && !isCreating ? (
                // Default empty state when no tasks at all
                <div className="flex flex-col items-center justify-center text-center pt-4 pb-8">
                  <div className="mb-2">
                    <Plus size={20} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-300">No tasks yet</p>
                </div>
              ) : (
                // Empty state for filtered tasks
                <EmptyTaskState
                  showUnscheduledOnly={showUnscheduledOnly}
                  showUnassignedOnly={showUnassignedOnly}
                  userFilters={userFilters}
                  isMobile={false}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
